import * as Sentry from "@sentry/react"
import { ToastProvider } from "@suraasa/placebo-ui"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import React from "react"
import {
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router-dom"

import { APIError } from "./api/utils"
import { getAuthInfo } from "./lib/auth"
import router from "./router"

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

if (SENTRY_DSN) {
  Sentry.init({
    ignoreErrors: [
      // Can't fix this
      "Failed to get document because the client is offline",
      "The play() request was interrupted by a call to pause()",
      "Error: The Google Maps JavaScript API could not load.",
    ],
    release: "web-suraasa-kind@" + APP_VERSION,
    dsn: SENTRY_DSN,
    tracesSampleRate: 0,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]],
        // Only capture errors from our own backend. This ignores errors from domains like:
        // https://suraasa-dev-cdn.azureedge.net
        // https://firebaseinstallations.googleapis.com
        failedRequestTargets: [/https:\/\/proxima\.suraasa\.com\/.*/],
      }),
      Sentry.replayIntegration({
        // Additional SDK configuration goes in here, for example:
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    sendDefaultPii: true,

    replaysSessionSampleRate: 0,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 0.5,

    beforeSend(event, hint) {
      // console.log("> Logging event on Sentry", event, hint)

      // Hydrating APIErrors with more information so it's easier to debug on Sentry Dashboard
      if (event.exception?.values && event.exception.values.length > 0) {
        const exception = event.exception.values[0]
        if (exception?.type === "APIError") {
          const originalException = hint.originalException as APIError
          event.tags = {
            ...event.tags,
            "api.status_code": originalException.statusCode?.toString() ?? "",
            "api.path": originalException.path ?? "",
            "api.message": originalException.message ?? "",
          }

          let body = originalException?.__errors?.config?.data || null
          try {
            body = JSON.parse(originalException?.__errors?.config?.data)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_) {
            /* empty */
          }

          event.contexts = {
            ...event.contexts,
            API: {
              url: originalException?.__errors?.config?.url,
              body,
              method: originalException?.__errors?.config?.method,
              statusCode: originalException?.statusCode,
              ...originalException?.errors,
            },
          }

          return event
        }
      }

      return event
    },
  })
  const authInfo = getAuthInfo()
  if (authInfo)
    Sentry.setUser({ id: authInfo.user.uuid, email: authInfo.user.email })
}

const App = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
      },
    },
  })
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider />
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}

export default App
